import { ReactNode, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { useStyles } from './SearchableCheckboxSelect.styles'

export interface SearchableCheckboxOption {
  value: string
  label: string
  /** Shown on hover in the dropdown (e.g. schema field description). */
  description?: string
}

interface SearchableCheckboxSelectProps {
  id: string
  label: string
  options: SearchableCheckboxOption[]
  value: string[]
  onChange: (next: string[]) => void
  /** When set to 1, acts as a single-select list (no radio adornment). */
  maxSelections?: number
  placeholder?: string
  /** When true, label floats inside the outlined field. */
  floatingLabel?: boolean
  disabled?: boolean
  /** Optional trailing control rendered inside the outlined field. */
  endAction?: ReactNode
}

/**
 * Searchable option picker. Multi-select uses square checkboxes; single-select
 * shows plain options (no radio) so picking one value is enough.
 */
export const SearchableCheckboxSelect = ({
  id,
  label,
  options,
  value,
  onChange,
  maxSelections,
  placeholder,
  floatingLabel = false,
  disabled = false,
  endAction = null,
}: SearchableCheckboxSelectProps) => {
  const { classes, cx } = useStyles()
  const [inputValue, setInputValue] = useState('')
  const [focused, setFocused] = useState(false)
  const singleSelect = maxSelections === 1
  /** Multi-select grows/wraps so chips never clip outside the field. */
  const allowChipWrap = !singleSelect

  const labelByValue = useMemo(
    () => new Map(options.map(o => [o.value, o.label] as const)),
    [options]
  )
  const descriptionByValue = useMemo(
    () =>
      new Map(
        options
          .filter(o => Boolean(o.description?.trim()))
          .map(o => [o.value, o.description!.trim()] as const)
      ),
    [options]
  )
  const optionValues = useMemo(() => options.map(o => o.value), [options])
  const hasValue = value.length > 0
  const float = floatingLabel && (focused || hasValue || Boolean(inputValue.trim()))

  const filterOptions = (
    opts: string[],
    state: { inputValue: string }
  ): string[] => {
    const query = state.inputValue.trim().toLowerCase()
    if (!query) return opts
    return opts.filter(option => {
      const display = (labelByValue.get(option) ?? option).toLowerCase()
      const desc = (descriptionByValue.get(option) ?? '').toLowerCase()
      return display.includes(query) || desc.includes(query)
    })
  }

  const wrapOption = (option: string, content: ReactNode) => {
    const tip = descriptionByValue.get(option)
    if (!tip) return content
    return (
      <Tooltip title={tip} arrow placement="right" enterDelay={400}>
        <span className={classes.optionTooltipTarget}>{content}</span>
      </Tooltip>
    )
  }

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
    option: string,
    { selected }: { selected: boolean }
  ) => {
    const { key, className, ...rest } = props
    const labelText = labelByValue.get(option) ?? option
    if (singleSelect) {
      return (
        <li
          key={key ?? option}
          {...rest}
          className={cx(className, classes.option)}
        >
          {wrapOption(option, labelText)}
        </li>
      )
    }
    return (
      <li
        key={key ?? option}
        {...rest}
        className={cx(className, classes.option)}
      >
        <Checkbox
          checked={selected}
          size="small"
          tabIndex={-1}
          disableRipple
          inputProps={{ 'aria-label': labelText }}
          sx={{ mr: 1, p: 0.5 }}
        />
        {wrapOption(option, labelText)}
      </li>
    )
  }

  const renderInput = (params: Parameters<
    NonNullable<React.ComponentProps<typeof Autocomplete>['renderInput']>
  >[0]) => (
    <TextField
      {...params}
      variant="outlined"
      label={floatingLabel ? label : undefined}
      placeholder={
        floatingLabel
          ? float
            ? ''
            : placeholder ?? ''
          : placeholder ?? ''
      }
      size="small"
      className={cx(
        floatingLabel && classes.floatFieldRoot,
        floatingLabel && float && classes.floatFieldRootFloated
      )}
      InputLabelProps={
        floatingLabel
          ? {
              shrink: float,
              className: classes.floatLabel,
              // Keep label inside the outline until focused/valued.
              disableAnimation: false,
            }
          : undefined
      }
      onFocus={event => {
        setFocused(true)
        params.inputProps.onFocus?.(event as never)
      }}
      onBlur={event => {
        setFocused(false)
        params.inputProps.onBlur?.(event as never)
      }}
      inputProps={{
        ...params.inputProps,
        'aria-label': label,
      }}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {endAction ? (
              <span className={classes.endAction}>{endAction}</span>
            ) : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  )

  const shared = {
    id,
    options: optionValues,
    inputValue,
    onInputChange: (_event: unknown, next: string) => setInputValue(next),
    clearOnBlur: false,
    handleHomeEndKeys: true as const,
    size: 'small' as const,
    getOptionLabel: (option: string) => labelByValue.get(option) ?? option,
    isOptionEqualToValue: (option: string, selected: string) =>
      option === selected,
    filterOptions,
    renderOption,
    renderInput,
    // Paper/listbox are portaled — set both slotProps and legacy props for MUI versions.
    slotProps: {
      paper: { className: classes.listboxPaper },
      listbox: { className: classes.listbox },
    },
    componentsProps: {
      paper: { className: classes.listboxPaper },
    },
    ListboxProps: { className: classes.listbox },
    PaperProps: { className: classes.listboxPaper },
  }

  if (singleSelect) {
    return (
      <div className={classes.root} data-testid={id}>
        <Autocomplete
          {...shared}
          disabled={disabled}
          multiple={false}
          value={value[0] ?? null}
          onChange={(_event, next) => {
            onChange(next ? [next] : [])
          }}
        />
      </div>
    )
  }

  return (
    <div
      className={cx(classes.root, allowChipWrap && classes.rootAllowWrap)}
      data-testid={id}
    >
      <Autocomplete
        {...shared}
        disabled={disabled}
        multiple
        disableCloseOnSelect
        value={value}
        onChange={(_event, next) => {
          if (
            typeof maxSelections === 'number' &&
            next.length > maxSelections
          ) {
            onChange(next.slice(-maxSelections))
            return
          }
          onChange(next)
        }}
      />
    </div>
  )
}
